import {
  afterRender,
  Component,
  computed,
  ElementRef,
  OnDestroy,
  OnInit,
  signal,
  viewChild,
} from '@angular/core';
import { Capacitor, type PluginListenerHandle } from '@capacitor/core';
import { Keyboard, KeyboardResize } from '@capacitor/keyboard';
import { IonContent, ModalController } from '@ionic/angular';
import { Chat } from '@ai-sdk/angular';
import {
  DefaultChatTransport,
  isToolUIPart,
  lastAssistantMessageIsCompleteWithApprovalResponses,
  type UIMessage,
} from 'ai';
import { TranslateService } from '@ngx-translate/core';
import { AuthService } from 'src/app/services/authService/auth.service';
import { DatetimeFormatService } from 'src/app/services/datetime-format-service/datetime-format.service';
import { AttendeesStore } from 'src/app/stores/attendees.store';
import { PlacesStore } from 'src/app/stores/places.store';
import { UserStore } from 'src/app/stores/user.store';
import { environment } from 'src/environments/environment';
import {
  loadAiChatHistory,
  saveAiChatHistory,
  type AiChatHistorySnapshot,
} from './ai-chat-history.storage';
import {
  buildMentionSuggestions,
  parseMessageSegments,
  textAlreadyHasPlaceEmail,
  type MentionKind,
  type MentionSuggestion,
} from './ai-chat-mention-format.util';
import {
  buildChatUserAvatarView,
  formatChatMessageTimestamp,
} from './ai-chat-utils';

@Component({
  selector: 'app-ai-chat',
  templateUrl: './ai-chat.component.html',
  styleUrls: ['./ai-chat.component.scss'],
})
export class AiChatComponent implements OnInit, OnDestroy {
  private readonly composeEditor =
    viewChild<ElementRef<HTMLElement>>('composeEditor');
  private readonly chatContent = viewChild(IonContent);

  readonly starterPromptKeys = [
    'aiChat.starters.whatCanYouDo',
    'aiChat.starters.calendarToday',
    'aiChat.starters.bookRoom',
    'aiChat.starters.cancelMeeting',
  ] as const;

  inputText = '';
  mentionSuggestions: MentionSuggestion[] = [];
  showMentionPopover = false;
  private mentionStartIndex = -1;

  readonly chat: Chat<UIMessage>;

  /** messageId → formatted timestamp; updated only when messages change */
  readonly timestamps = signal<Record<string, string>>({});
  readonly hasPendingToolApproval = signal(false);
  readonly showWaiting = signal(false);
  /** Latest in-flight tool part; only this one may show a working label. */
  readonly currentWorkingToolPart = signal<{
    messageId: string;
    partIndex: number;
  } | null>(null);
  readonly hasResumableConversation = signal(false);
  readonly approvalLocale = computed(() =>
    this.datetimeFormatService.selectedDateTimeFormat(),
  );
  readonly attendeesList = computed(() => this.attendeesStore.attendees());
  readonly placesList = computed(() => this.placesStore.places());

  readonly userAvatar = computed(() => {
    this.attendeesStore.attendees();
    return buildChatUserAvatarView(this.userStore.user(), (upn) =>
      this.attendeesStore.findAttendeeAvatar(upn),
    );
  });

  private syncKey = '';
  private readonly messageSentAtMs = new Map<string, number>();
  private savedSnapshot: AiChatHistorySnapshot | null = null;
  private persistTimer?: ReturnType<typeof setTimeout>;
  private keyboardListeners: PluginListenerHandle[] = [];
  private previousKeyboardResize?: KeyboardResize;

  constructor(
    private hostRef: ElementRef<HTMLElement>,
    private modalController: ModalController,
    private authService: AuthService,
    private attendeesStore: AttendeesStore,
    private placesStore: PlacesStore,
    private userStore: UserStore,
    private datetimeFormatService: DatetimeFormatService,
    private translate: TranslateService,
  ) {
    this.chat = new Chat<UIMessage>({
      transport: new DefaultChatTransport({
        api: environment.baseUrl + '/ai/ai/chat',
        // api: 'http://localhost:3000/ai/chat',
        headers: async () => {
          const token = await this.authService.fetchValidToken();
          const headers: Record<string, string> = {
            'x-user-timezone':
              Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
          };
          if (token) {
            headers['Authorization'] = `Bearer ${token}`;
          }
          return headers;
        },
      }),
      sendAutomaticallyWhen: lastAssistantMessageIsCompleteWithApprovalResponses,
      onFinish: () => {
        this.schedulePersist();
        setTimeout(() => this.focusComposeEditor(), 100);
      },
    });

    afterRender(() => {
      this.syncCurrentWorkingToolPart();
      this.syncChromeState();

      // Follow the stream as tokens arrive (syncKey may not change while streaming).
      if (
        this.chat.status === 'streaming' ||
        this.chat.status === 'submitted'
      ) {
        void this.scrollToBottom(0);
      }
    });
  }

  ngOnInit(): void {
    void this.loadResumableSnapshot();
    void this.bindKeyboardInset();
  }

  ngOnDestroy(): void {
    void this.unbindKeyboardInset();
  }

  focusComposeEditor(): void {
    this.composeEditor()?.nativeElement.focus();
  }

  sendMessage(): void {
    const editor = this.composeEditor()?.nativeElement;
    const text = (
      editor ? this.serializeComposeContent(editor) : this.inputText
    ).trim();
    if (
      !text ||
      this.chat.status !== 'ready' ||
      this.hasPendingToolApproval()
    ) {
      return;
    }

    this.inputText = '';
    this.clearComposeEditor();
    this.clearMentionPopover();
    void this.chat.sendMessage({ text });
    this.scrollToBottomAfterLayout();
  }

  sendStarter(i18nKey: string): void {
    if (this.chat.status !== 'ready' || this.hasPendingToolApproval()) {
      return;
    }

    const text = this.translate.instant(i18nKey).trim();
    if (!text) {
      return;
    }

    this.inputText = '';
    this.clearComposeEditor();
    this.clearMentionPopover();
    void this.chat.sendMessage({ text });
    this.scrollToBottomAfterLayout();
  }

  onComposeChange(): void {
    const editor = this.composeEditor()?.nativeElement;
    if (editor) {
      this.inputText = this.serializeComposeContent(editor);
      this.normalizeComposePlaceholder(editor);
    }
    this.updateMentionSuggestions();
  }

  onComposeFocus(): void {
    // Keyboard inset handler also scrolls; this covers slower platforms.
    setTimeout(() => this.scrollToBottomAfterLayout(), 300);
  }

  onComposeKeyDown(event: KeyboardEvent): void {
    if (event.key !== 'Enter' || event.shiftKey) {
      return;
    }
    event.preventDefault();
    if (this.showMentionPopover) {
      return;
    }
    this.sendMessage();
  }

  selectMention(suggestion: MentionSuggestion): void {
    const editor = this.composeEditor()?.nativeElement;
    if (!editor || this.mentionStartIndex < 0) {
      return;
    }

    const text = this.serializeComposeContent(editor);
    if (
      suggestion.kind === 'resource' &&
      textAlreadyHasPlaceEmail(text, this.placesStore.places())
    ) {
      return;
    }

    const mentionFragment = text
      .slice(this.mentionStartIndex)
      .match(/^@([^\s@]*)/);
    if (!mentionFragment) {
      return;
    }

    const queryEnd = this.mentionStartIndex + mentionFragment[0].length;
    const prefix = text.slice(0, this.mentionStartIndex);
    const suffix = text.slice(queryEnd);
    const nextText = `${prefix}${suggestion.value} ${suffix}`.replace(
      /\s{2,}/g,
      ' ',
    );

    this.inputText = nextText;
    this.renderComposeFromText(nextText);
    this.placeCursorAtEnd();
    this.clearMentionPopover();
  }

  stop(): void {
    this.chat.stop();
  }

  resumeConversation(): void {
    const snapshot = this.savedSnapshot;
    if (!snapshot?.messages.length) {
      return;
    }

    this.chat.messages = snapshot.messages;
    this.hasResumableConversation.set(false);
    this.syncKey = '';
    this.syncChromeState();
    void this.scrollToBottom(0);
    this.composeEditor()?.nativeElement.focus();
  }

  async dismiss(): Promise<void> {
    await this.persistNow();
    await this.modalController.dismiss();
  }

  respondToApproval(approvalId: string, approved: boolean): void {
    void this.chat.addToolApprovalResponse({
      id: approvalId,
      approved,
      reason: approved
        ? 'User confirmed the action.'
        : 'User cancelled in the confirmation card. Acknowledge briefly and ask how they want to continue. Do not retry.',
    });
  }

  /** Deny create_event so the model asks what to change, then retries with edits. */
  respondToApprovalEdit(approvalId: string): void {
    void this.chat.addToolApprovalResponse({
      id: approvalId,
      approved: false,
      reason: APPROVAL_EDIT_REASON,
    });
  }

  approvalResponseLabelKey(approval: {
    approved: boolean;
    reason?: string;
  }): string {
    if (approval.approved) {
      return 'aiChat.approval.approved';
    }
    if (isApprovalEditReason(approval.reason)) {
      return 'aiChat.approval.editing';
    }
    return 'aiChat.approval.cancelled';
  }

  isEditApproval(reason?: string): boolean {
    return isApprovalEditReason(reason);
  }

  private updateMentionSuggestions(): void {
    const editor = this.composeEditor()?.nativeElement;
    const text = editor
      ? this.serializeComposeContent(editor)
      : this.inputText;
    const cursorPosition = editor
      ? this.getComposeCaretOffset(editor)
      : text.length;
    const textToCursor = text.slice(0, cursorPosition);
    const match = textToCursor.match(/(?:^|[\s,(])@([^\s@]*)$/);

    if (!match) {
      this.clearMentionPopover();
      return;
    }

    const query = (match[1] ?? '').trim();
    this.mentionStartIndex = textToCursor.lastIndexOf('@', cursorPosition);

    const suggestions = buildMentionSuggestions(
      query,
      this.attendeesStore.attendees(),
      this.placesStore.places(),
      (attendee, searchTerm) =>
        this.attendeesStore.findAttendee(attendee, searchTerm) === true,
      (place, searchTerm) =>
        this.placesStore.findMatchingPlaces(place, searchTerm) === true,
      8,
      {
        allowResource: !textAlreadyHasPlaceEmail(
          text,
          this.placesStore.places(),
        ),
      },
    );

    this.mentionSuggestions = suggestions;
    this.showMentionPopover = suggestions.length > 0;
  }

  private clearMentionPopover(): void {
    this.showMentionPopover = false;
    this.mentionSuggestions = [];
    this.mentionStartIndex = -1;
  }

  /** Turn editor DOM (text + chips) into plain message text with emails. */
  private serializeComposeContent(root: HTMLElement): string {
    let text = '';

    root.childNodes.forEach((node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        text += node.textContent ?? '';
        return;
      }

      if (!(node instanceof HTMLElement)) {
        return;
      }

      if (node.classList.contains('input-mention-chip')) {
        text += node.dataset['value'] ?? '';
        return;
      }

      if (node.tagName === 'BR') {
        text += '\n';
        return;
      }

      if (node.tagName === 'DIV' || node.tagName === 'P') {
        if (text.length > 0 && !text.endsWith('\n')) {
          text += '\n';
        }
        text += this.serializeComposeContent(node);
        return;
      }

      text += this.serializeComposeContent(node);
    });

    return text;
  }

  private renderComposeFromText(text: string): void {
    const editor = this.composeEditor()?.nativeElement;
    if (!editor) {
      return;
    }

    editor.innerHTML = '';
    if (!text) {
      editor.classList.add('is-placeholder');
      return;
    }
    editor.classList.remove('is-placeholder');

    const segments = parseMessageSegments(
      text,
      this.attendeesStore.attendees(),
      this.placesStore.places(),
    );

    for (const segment of segments) {
      if (segment.type === 'mention') {
        editor.appendChild(
          this.createMentionChipElement(
            segment.kind,
            segment.label,
            segment.value,
            segment.icon,
          ),
        );
        editor.appendChild(document.createTextNode('\u00A0'));
        continue;
      }

      const lines = segment.value.split('\n');
      lines.forEach((line, lineIndex) => {
        if (line) {
          editor.appendChild(document.createTextNode(line));
        }
        if (lineIndex < lines.length - 1) {
          editor.appendChild(document.createElement('br'));
        }
      });
    }
  }

  private createMentionChipElement(
    kind: MentionKind,
    label: string,
    value: string,
    icon: string,
  ): HTMLSpanElement {
    const chip = document.createElement('span');
    chip.className = `input-mention-chip input-mention-chip--${kind}`;
    chip.contentEditable = 'false';
    chip.dataset['mentionKind'] = kind;
    chip.dataset['value'] = value;

    const iconElement = document.createElement('ion-icon');
    iconElement.setAttribute('name', icon);
    chip.appendChild(iconElement);

    const labelElement = document.createElement('span');
    labelElement.className = 'input-mention-chip-label';
    labelElement.textContent = label;
    chip.appendChild(labelElement);

    return chip;
  }

  private clearComposeEditor(): void {
    const editor = this.composeEditor()?.nativeElement;
    if (editor) {
      editor.innerHTML = '';
      editor.classList.add('is-placeholder');
    }
  }

  /**
   * contenteditable leaves <br> / empty wrappers after delete-all, which breaks
   * the CSS :empty placeholder. Normalize and toggle .is-placeholder instead.
   */
  private normalizeComposePlaceholder(editor: HTMLElement): void {
    if (this.inputText.trim()) {
      editor.classList.remove('is-placeholder');
      return;
    }

    if (editor.innerHTML !== '') {
      editor.innerHTML = '';
    }
    this.inputText = '';
    editor.classList.add('is-placeholder');
  }

  private placeCursorAtEnd(): void {
    const editor = this.composeEditor()?.nativeElement;
    if (!editor) {
      return;
    }

    editor.focus();
    const selection = window.getSelection();
    if (!selection) {
      return;
    }

    const range = document.createRange();
    range.selectNodeContents(editor);
    range.collapse(false);
    selection.removeAllRanges();
    selection.addRange(range);
  }

  /** Map the live caret into an offset in the serialized plain text. */
  private getComposeCaretOffset(editor: HTMLElement): number {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0 || !editor.contains(selection.anchorNode)) {
      return this.serializeComposeContent(editor).length;
    }

    const range = selection.getRangeAt(0).cloneRange();
    const pre = range.cloneRange();
    pre.selectNodeContents(editor);
    pre.setEnd(range.startContainer, range.startOffset);

    const probe = document.createElement('div');
    probe.appendChild(pre.cloneContents());
    return this.serializeComposeContent(probe).length;
  }

  private syncCurrentWorkingToolPart(): void {
    const next = this.findCurrentWorkingToolPart();
    const current = this.currentWorkingToolPart();
    if (
      current?.messageId === next?.messageId &&
      current?.partIndex === next?.partIndex
    ) {
      return;
    }
    this.currentWorkingToolPart.set(next);
  }

  private findCurrentWorkingToolPart(): {
    messageId: string;
    partIndex: number;
  } | null {
    if (this.chat.status !== 'submitted' && this.chat.status !== 'streaming') {
      return null;
    }

    const lastMessage = this.chat.messages.at(-1);
    if (!lastMessage || lastMessage.role !== 'assistant') {
      return null;
    }

    let lastToolIndex = -1;
    for (let i = 0; i < lastMessage.parts.length; i++) {
      if (lastMessage.parts[i].type === 'dynamic-tool') {
        lastToolIndex = i;
      }
    }
    if (lastToolIndex < 0) {
      return null;
    }

    return { messageId: lastMessage.id, partIndex: lastToolIndex };
  }

  private syncChromeState(): void {
    const messages = this.chat.messages;
    const pending = messages.some((message) =>
      message.parts.some(
        (part) => isToolUIPart(part) && part.state === 'approval-requested',
      ),
    );
    const key = `${messages.map((m) => m.id).join(',')}|${this.chat.status}|${pending ? 1 : 0}|${this.datetimeFormatService.selectedDateTimeFormat()}`;
    if (key === this.syncKey) {
      return;
    }
    this.syncKey = key;

    const locale = this.datetimeFormatService.selectedDateTimeFormat();
    const activeIds = new Set<string>();
    const next: Record<string, string> = {};
    for (const message of messages) {
      activeIds.add(message.id);
      if (!this.messageSentAtMs.has(message.id)) {
        this.messageSentAtMs.set(message.id, Date.now());
      }
      next[message.id] = formatChatMessageTimestamp(
        this.messageSentAtMs.get(message.id)!,
        locale,
      );
    }
    for (const id of this.messageSentAtMs.keys()) {
      if (!activeIds.has(id)) {
        this.messageSentAtMs.delete(id);
      }
    }

    this.timestamps.set(next);
    this.hasPendingToolApproval.set(pending);
    // Only while the request is in-flight before tokens start streaming.
    this.showWaiting.set(this.chat.status === 'submitted' && !pending);

    if (messages.length > 0 || this.showWaiting()) {
      this.scrollToBottomAfterLayout();
    }
  }

  private async scrollToBottom(durationMs = 300): Promise<void> {
    const content = this.chatContent();
    if (!content) {
      return;
    }
    try {
      await content.scrollToBottom(durationMs);
    } catch {
      // Content may not be ready yet (e.g. first paint).
    }
  }

  /** Scroll now, then again after markdown/tool cards finish laying out. */
  private scrollToBottomAfterLayout(): void {
    void this.scrollToBottom(0);
    requestAnimationFrame(() => {
      void this.scrollToBottom(0);
      setTimeout(() => void this.scrollToBottom(0), 120);
      setTimeout(() => void this.scrollToBottom(0), 320);
    });
  }

  /**
   * Fullscreen modals often don't shrink with KeyboardResize.Ionic.
   * Pad the chat host by the keyboard height instead (simple + reliable).
   */
  private async bindKeyboardInset(): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    try {
      const current = await Keyboard.getResizeMode();
      this.previousKeyboardResize = current.mode;
    } catch {
      // iOS-only API on some versions.
    }

    // Avoid double-offset: we inset manually, so disable system resize here.
    try {
      await Keyboard.setResizeMode({ mode: KeyboardResize.None });
    } catch {
      // Ignore if unsupported.
    }

    try {
      this.keyboardListeners = [
        await Keyboard.addListener('keyboardWillShow', (info) => {
          this.setKeyboardInset(info.keyboardHeight);
          this.scrollToBottomAfterLayout();
        }),
        await Keyboard.addListener('keyboardWillHide', () => {
          this.setKeyboardInset(0);
        }),
      ];
    } catch {
      // Keyboard plugin unavailable.
    }
  }

  private async unbindKeyboardInset(): Promise<void> {
    for (const listener of this.keyboardListeners) {
      await listener.remove();
    }
    this.keyboardListeners = [];
    this.setKeyboardInset(0);

    if (this.previousKeyboardResize != null) {
      try {
        await Keyboard.setResizeMode({ mode: this.previousKeyboardResize });
      } catch {
        // Ignore restore failures.
      }
      this.previousKeyboardResize = undefined;
    }
  }

  private setKeyboardInset(heightPx: number): void {
    this.hostRef.nativeElement.style.setProperty(
      '--ai-chat-keyboard-inset',
      `${Math.max(0, Math.round(heightPx))}px`,
    );
  }

  private currentUserId(): string {
    const user = this.userStore.user();
    return user?.microsoftUserId || user?.userPrincipalName || '';
  }

  private async loadResumableSnapshot(): Promise<void> {
    const userId = this.currentUserId();
    const snapshot = await loadAiChatHistory(userId);
    this.savedSnapshot = snapshot;
    this.hasResumableConversation.set(Boolean(snapshot?.messages.length));
  }

  private schedulePersist(): void {
    if (this.persistTimer) {
      clearTimeout(this.persistTimer);
    }
    this.persistTimer = setTimeout(() => {
      this.persistTimer = undefined;
      void this.persistNow();
    }, 400);
  }

  private async persistNow(): Promise<void> {
    if (this.persistTimer) {
      clearTimeout(this.persistTimer);
      this.persistTimer = undefined;
    }

    const userId = this.currentUserId();
    const messages = this.chat.messages;
    if (!userId || messages.length === 0) {
      return;
    }

    await saveAiChatHistory(userId, messages);
    this.savedSnapshot = { messages, updatedAt: Date.now() };
    this.hasResumableConversation.set(false);
  }
}

/** Denial reason when the user taps Edit on the create-event approval card. */
const APPROVAL_EDIT_REASON =
  'User chose Edit. Ask only what they want to change, keep the other fields, then call the tool again with the updates.';

function isApprovalEditReason(reason?: string): boolean {
  return (reason ?? '').startsWith('User chose Edit');
}
